import React, { useEffect, useState } from 'react'
import { GoogleMap, MarkerF, PolylineF, useJsApiLoader } from "@react-google-maps/api"
import { MarkerImage, MarkerLocation, WalkNote } from "../../Interfaces"
import { GOOGLE_MAPS_CONFIG } from '../../config/googleMapsConfig'
import { usePublicSiteBranding } from '../../contexts/PublicSiteContext'
import {
  displayLatLngForMarker,
  orderedWalkMarkers,
} from '../../utils/mapMarkers'

/** GPS / camera marker — fill matches site accent (orange vs Patrol red). */
function buildPhotoTaskMarkerSvg(fill: string): string {
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">' +
    '<circle cx="20" cy="20" r="18" fill="' +
    fill +
    '" stroke="#fff" stroke-width="2"/>' +
    '<path fill="#fff" transform="translate(8,11) scale(0.92)" d="M9 3L7.17 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2h-3.17L15 3H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>' +
    '</svg>'
  )
}

function photoTaskMapIcon(fill: string): google.maps.Icon {
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(buildPhotoTaskMarkerSvg(fill)),
    scaledSize: new google.maps.Size(40, 40),
    anchor: new google.maps.Point(20, 20),
  }
}

interface MapProps {
  name?: string
  zoom?: number
  setZoom?: (zoom: number) => void
  coordinates?: google.maps.LatLngLiteral | google.maps.LatLng
  handleLocationAdjustment?: Function
  setMarkers?: (markers: MarkerLocation[]) => void
  markers?: MarkerLocation[]
  walkNotes?: WalkNote[]
  walkImages?: MarkerImage[]
  handleNoteClick?: (notes: WalkNote[]) => void
  handleImageClick?: (images: MarkerImage[]) => void
  animate?: boolean
  onDoubleClick?: (latLng: google.maps.LatLngLiteral) => void
}

export default function Map({
  name,
  zoom = 15,
  setZoom,
  coordinates = { lat: 0, lng: 0 },
  handleLocationAdjustment,
  setMarkers,
  markers,
  walkNotes,
  walkImages,
  handleNoteClick,
  handleImageClick,
  animate = false,
  onDoubleClick
}: MapProps) {
  const { mapMarkerFillHex } = usePublicSiteBranding()
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_CONFIG)
  const [map, setMap] = useState<google.maps.Map>()
  const [position, setPosition] = useState<google.maps.LatLngLiteral | google.maps.LatLng>(coordinates)
  const [path, setPath] = useState<google.maps.LatLngLiteral[]>([])

  useEffect(() => {
    setPosition(coordinates)
  }, [coordinates])

  useEffect(() => {
    const list = orderedWalkMarkers(markers)
    const fb = {
      lat: typeof coordinates.lat === 'number' ? coordinates.lat : Number(coordinates.lat),
      lng: typeof coordinates.lng === 'number' ? coordinates.lng : Number(coordinates.lng),
    }
    const newPath = list.map((m, i) =>
      displayLatLngForMarker(m, fb, i, list.length),
    ) as google.maps.LatLngLiteral[]
    setPath(newPath)
  }, [markers, coordinates])

  useEffect(() => {
    if (map && setZoom) {
      map.setZoom(zoom)
    }
  }, [zoom, map, setZoom])

  const onLoad = (map: google.maps.Map) => {
    setMap(map)
  }

  const onUnmount = () => {
    setMap(undefined)
  }

  // height: 100% alone is often 0px in flex layouts without a sized ancestor; minHeight makes tiles visible.
  const containerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    minHeight: "min(50vh, 800px)",
  }

  if (!GOOGLE_MAPS_CONFIG.googleMapsApiKey) {
    return (
      <div
        className="flex w-full items-center justify-center rounded border border-amber-200 bg-amber-50 p-4 text-amber-900"
        style={{ minHeight: "min(50vh, 800px)" }}
      >
        <p className="text-center text-sm">
          Map unavailable: set <code className="rounded bg-amber-100 px-1">REACT_APP_GOOGLE_MAPS_API_KEY</code> in{" "}
          <code className="rounded bg-amber-100 px-1">.env</code> and restart the dev server.
        </p>
      </div>
    )
  }

  if (loadError) {
    return (
      <div
        className="flex w-full items-center justify-center rounded border border-red-200 bg-red-50 p-4 text-red-900"
        style={{ minHeight: "min(50vh, 800px)" }}
      >
        <p className="max-w-md text-center text-sm">
          Google Maps could not load ({loadError.message}). Enable the Maps JavaScript API for this key and allow your
          origin (e.g. <code className="rounded bg-red-100 px-1">http://localhost:3000</code>) in API key HTTP referrer
          restrictions.
        </p>
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div
        className="flex w-full items-center justify-center bg-slate-100 text-slate-600"
        style={{ minHeight: "min(50vh, 800px)" }}
      >
        <p className="text-sm">Loading map…</p>
      </div>
    )
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={position}
      zoom={zoom}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{
        gestureHandling: 'cooperative',
        disableDoubleClickZoom: true
      }}
      onIdle={() => {
        if (!map || !handleLocationAdjustment) return
        const center = map.getCenter()
        if (!center) return
        handleLocationAdjustment({
          lat: center.lat(),
          lng: center.lng(),
        })
      }}
      onDblClick={(e) => {
        if (onDoubleClick && e.latLng) {
          onDoubleClick({
            lat: e.latLng.lat(),
            lng: e.latLng.lng()
          })
        }
      }}
    >
      {orderedWalkMarkers(markers).map((marker, index) => {
        const fb = {
          lat: typeof coordinates.lat === 'number' ? coordinates.lat : Number(coordinates.lat),
          lng: typeof coordinates.lng === 'number' ? coordinates.lng : Number(coordinates.lng),
        }
        const pos = displayLatLngForMarker(marker, fb, index, markers?.length || 0)
        return (
        <MarkerF
          key={`${marker.id ?? 'id'}-${marker.label}-${index}`}
          position={pos}
          label={String(marker.label ?? index + 1)}
          icon={marker.photoTaskType ? photoTaskMapIcon(mapMarkerFillHex) : undefined}
          draggable={true}
          onDragEnd={(e) => {
            if (e.latLng && setMarkers && markers) {
              const updatedMarkers = markers.map(m => {
                if (m.label === marker.label) {
                  return {
                    ...m,
                    lat: e.latLng!.lat(),
                    lng: e.latLng!.lng()
                  }
                }
                return m
              })
              setMarkers(updatedMarkers)
            }
          }}
          onClick={() => {
            if (handleNoteClick && marker.notes) {
              handleNoteClick(marker.notes)
            }
            if (handleImageClick && marker.images) {
              handleImageClick(marker.images)
            }
          }}
        />
        )
      })}
      {path.length > 1 && (
        <PolylineF
          path={path}
          options={{
            strokeColor: "#FF0000",
            strokeOpacity: 1.0,
            strokeWeight: 2
          }}
        />
      )}
    </GoogleMap>
  )
}
